// Обёртка над fetch для /api/operator/*.
// Фичи:
//   • прозрачно проставляет Authorization: Bearer из localStorage
//   • при 401 однократно пробует обновить access через refresh-token
//   • после неудачного refresh — logout + redirect на /login
//   • парсит ошибки бекенда (JSON c полем error) и бросает понятный Error
//
// Все страницы sadmin ходят в бекенд только через эту функцию — чтобы единая
// точка обработки ошибок, редиректов и отмен.

import { getAccessToken, logout, performRefresh } from './auth';

const API_ORIGIN = (import.meta.env.VITE_API_URL?.trim() || '').replace(/\/+$/, '');
const API_PREFIX = `${API_ORIGIN}/api/operator`;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly details: unknown;

  constructor(message: string, status: number, code?: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface Options extends RequestInit {
  /** Если true — не пытаемся рефрешить и не редиректим на /login при 401. */
  skipAuthRedirect?: boolean;
}

async function requestOnce(
  url: string,
  init: RequestInit,
  token: string | null,
): Promise<Response> {
  const headers = new Headers(init.headers ?? {});
  if (!headers.has('Content-Type') && init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return fetch(url, { ...init, headers });
}

async function parseError(res: Response): Promise<ApiError> {
  const text = await res.text().catch(() => '');
  let message = res.statusText || `HTTP ${res.status}`;
  let code: string | undefined;
  let details: unknown;
  try {
    const json = JSON.parse(text) as { error?: string; code?: string; details?: unknown };
    if (json.error) message = json.error;
    if (json.code) code = json.code;
    details = json.details;
  } catch {
    if (text) message = text;
  }
  return new ApiError(message, res.status, code, details);
}

export async function api<T>(path: string, init: Options = {}): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_PREFIX}${path}`;
  const { skipAuthRedirect, ...rest } = init;

  let token = getAccessToken();
  let res = await requestOnce(url, rest, token);

  // Однократный повтор при 401: пытаемся обновить access через refresh.
  if (res.status === 401 && !skipAuthRedirect) {
    const newToken = await performRefresh();
    if (newToken) {
      token = newToken;
      res = await requestOnce(url, rest, token);
    }
    if (res.status === 401) {
      logout();
      throw new ApiError('Требуется вход', 401);
    }
  }

  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return (await res.json()) as T;
  }
  return (await res.text()) as unknown as T;
}

export const apiGet = <T>(path: string) => api<T>(path);
export const apiPost = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined });
export const apiPatch = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PATCH', body: body !== undefined ? JSON.stringify(body) : undefined });
export const apiPut = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PUT', body: body !== undefined ? JSON.stringify(body) : undefined });
export const apiDelete = <T>(path: string) => api<T>(path, { method: 'DELETE' });
